import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  LegacyText,
  HStack,
} from "flicket-ui";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  BaseContext,
  ReservationActionTypes,
  ReservationContext,
} from "~context";
import BuyNowPayLater from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import { MembershipQuery, IntegrationType } from "~graphql/sdk";
import { useAccount, useOrganization } from "~hooks";
import { OrganizationFeatures } from "~lib";
import { AddonListItem, ListItem } from "./item";
import { Option } from "~components/common";
import { SeatsIOSeat } from "../constants";
import { QuantitySetValidationModal } from "../common/common.QuantitySetValidation.modal";
import { PaymentPlanBadge } from "~features/seated-reservation/components/common/common.PaymentPlanBadge";
import { Cart } from "~features/checkout/cart";
import { ExpressCheckoutButtonRow } from "~features/express-checkout/components/ExpressCheckoutButtonRow";
import { CreateOrderOptions } from "~hooks/reservation/useCreateOrder";
import { addQueryParams } from "~lib/helpers/queryParams";
import { useRouter } from "next/router";

type QuantitySetValidation = {
  count: number;
  quantitySet: number;
  valid: boolean;
  name: string;
};

type PricingProps = {
  addons?: any[];
  changingSeatsFee?: number;
  changeTickets: any[];
  navigate: (options?: CreateOrderOptions) => void;
  isNavigating?: boolean;
  getPrice: (seat: SeatsIOSeat) => [number, number];
  noSeatsAvailable: boolean;
  onBack: () => void;
  priceComp: number | undefined;
  validSeatSelection: boolean;
  paymentPlanSettings: MembershipQuery["membership"]["paymentPlanSettings"];
  fees: MembershipQuery["membership"]["fees"];
  multiBuyPromotions: MembershipQuery["membership"]["multiBuyPromotions"];
};

export const Pricing = ({
  addons: addonOptions,
  changingSeatsFee,
  changeTickets,
  navigate,
  isNavigating,
  getPrice,
  noSeatsAvailable,
  onBack,
  priceComp,
  validSeatSelection,
  paymentPlanSettings,
  fees,
  multiBuyPromotions,
}: PricingProps) => {
  const { organization, t, hasFeature } = useOrganization();
  const router = useRouter();

  const { seats, addons, dispatch, availableMembershipTypes } = useContext(
    ReservationContext
  );

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);

  const { isPOS, isAdmin } = useAccount();
  const { isSinglePage } = useContext(BaseContext);

  const showHiddenFees = hasFeature(OrganizationFeatures.ShowHiddenFees);

  const ticketingOptions: Option[] = availableMembershipTypes.map(
    ({ id, name, ...rest }) => ({
      label: name,
      value: id,
    })
  );

  const availableAddons = useMemo(
    () => addonOptions?.filter(({ isActive }) => isActive),
    [addonOptions]
  );

  const cart = new Cart(fees, multiBuyPromotions);
  cart.isPos(isPOS);
  if (
    !hasFeature(OrganizationFeatures.OrderSmsConfirmationDisabled) &&
    !hasFeature(OrganizationFeatures.OrderSmsConfirmationDefaultOptOut) &&
    !isAdmin &&
    !isPOS
  ) {
    cart.sendSmsConfirmation(true);
  }

  cart.includeFees(showHiddenFees);

  seats?.forEach((seat) => {
    const [price] = getPrice(seat);
    seat.ticketType.fee = cart.getItemFee(seat.ticketType.id, price);

    cart.add(seat.ticketType.id, price);
  });

  availableAddons?.forEach((addon) => {
    const quantity = addons?.[addon.id]?.value;

    if (quantity) {
      cart.add(addon.id, addon.price, quantity);
    }
  });

  const updateSeat = useCallback(
    (id, ticketId) => {
      const ticketType = availableMembershipTypes.find(
        (v) => v.id === ticketId
      );

      dispatch({
        type: ReservationActionTypes.SET_SEAT_TYPE,
        payload: { id, ticketType },
      });
    },
    [dispatch]
  );

  const updateAddon = useCallback(
    (id, quantity) =>
      dispatch({
        type: ReservationActionTypes.UPDATE_ADDON,
        payload: { id, quantity },
      }),
    [dispatch]
  );

  let pricingOptions;

  if (seats?.length) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeat}
        options={ticketingOptions}
        getPrice={getPrice}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else {
    pricingOptions = <LegacyText mb={3}>Please select your seating</LegacyText>;
  }

  const onCloseQuantitySetValidationModal = () => {
    setQuantitySetValidationError(null);
  };

  const validateQuantitySets = (
    seats: SeatsIOSeat[],
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    const validationMap = new Map<string, QuantitySetValidation>();

    for (const { ticketType } of seats) {
      if (!ticketType?.quantitySet) continue;

      const currentEntry = validationMap.get(ticketType.id);
      const currentCount = currentEntry?.count || 0;
      const newCount = currentCount + 1;
      const isValid = newCount % ticketType.quantitySet === 0;

      validationMap.set(ticketType.id, {
        count: newCount,
        valid: isValid,
        quantitySet: ticketType.quantitySet,
        name: ticketType.name,
      });
    }

    for (const entry of validationMap.values()) {
      if (!entry.valid) {
        setQuantitySetValidationError({
          name: entry.name,
          quantitySet: entry.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = (options: CreateOrderOptions = {}) => {
    if (!seats) return;

    const isValid = validateQuantitySets(seats, setQuantitySetValidationError);

    if (isValid) {
      if (options.isExpressCheckout) {
        addQueryParams(router, {
          modal: "express-checkout",
          paymentType: options.paymentType,
        });
      }
      return navigate(options);
    }
  };

  return (
    <Box>
      <QuantitySetValidationModal
        isOpen={!!quantitySetValidationError}
        onClose={onCloseQuantitySetValidationModal}
        quantitySet={quantitySetValidationError?.quantitySet}
        name={quantitySetValidationError?.name}
      />
      <LegacyText
        fontWeight="heavy"
        fontSize={6}
        color="N800"
        lineHeight="normal"
      >
        Your selection
      </LegacyText>

      <Divider my={2} />

      {pricingOptions}

      {!noSeatsAvailable && seats?.length > 0 && (
        <>
          {!!availableAddons?.length && (
            <Box mb={2} mt={4}>
              <LegacyText
                fontWeight="heavy"
                fontSize={6}
                color="N800"
                lineHeight="normal"
              >
                Available Addons
              </LegacyText>
              <Divider my={2} />
              {availableAddons.map(({ id, name, price, ...rest }) => (
                <AddonListItem
                  {...rest}
                  id={id}
                  key={id}
                  name={name}
                  price={price}
                  quantity={addons?.[id] || 0}
                  onChange={(value) => {
                    updateAddon(id, value);
                  }}
                />
              ))}
            </Box>
          )}

          {!!changeTickets?.length && (
            <Box mb={2}>
              <Divider />
              {!!changingSeatsFee && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <LegacyText fontWeight="extraBold" fontSize={3} color="N700">
                    Reservation change fee
                  </LegacyText>
                  <LegacyText fontWeight="medium" fontSize={2} color="N600">
                    <Price
                      price={changingSeatsFee}
                      currency={organization.currency}
                    />
                  </LegacyText>
                </Flex>
              )}
              {!!priceComp && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <LegacyText fontWeight="extraBold" fontSize={3} color="N700">
                    Price difference compensation
                  </LegacyText>
                  <LegacyText fontWeight="medium" fontSize={2} color="N600">
                    <Price
                      price={-priceComp}
                      currency={organization.currency}
                    />
                  </LegacyText>
                </Flex>
              )}
            </Box>
          )}

          <Divider />

          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Total tickets
            </LegacyText>
            <LegacyText
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {seats?.length ?? 0}
            </LegacyText>
          </Flex>

          {cart.multibuyDiscount > 0 && (
            <>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <LegacyText fontWeight="medium" fontSize={2} color="N600">
                  Multibuy promotion applied
                </LegacyText>
                <Price
                  price={-cart.multibuyDiscount}
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                  currency={organization.currency}
                />
              </Flex>
              <Divider my={{ _: 3, md: 2 }} />
            </>
          )}

          <Flex mt={2} justifyContent="space-between" w="100%">
            <LegacyText fontWeight="medium" fontSize={2} color="N600">
              Total {showHiddenFees ? "(including booking fees)" : ""}
            </LegacyText>
            <LegacyText
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {seats ? (
                <Price price={cart.total} currency={organization.currency} />
              ) : (
                "-"
              )}
            </LegacyText>
          </Flex>

          {!isPOS && cart.total > 0 && (
            <>
              {cart.afterPay > 0 && (
                <Box mt={2} w="100%">
                  <BuyNowPayLater
                    totalPrice={cart.afterPay}
                    hasTicketSelected={!!seats}
                    bnplIntegration={{
                      type: IntegrationType.PaymentAfterpay,
                    }}
                  />
                </Box>
              )}
              {cart.stripeAfterPay > 0 && (
                <Box mt={2}>
                  <BuyNowPayLater
                    totalPrice={cart.stripeAfterPay}
                    hasTicketSelected={!!seats}
                    bnplIntegration={{
                      type: IntegrationType.PaymentAfterpay,
                    }}
                  />
                </Box>
              )}
            </>
          )}

          {paymentPlanSettings && !paymentPlanSettings.disabledAt && (
            <Box mt={2}>
              <PaymentPlanBadge
                scheduleOptions={paymentPlanSettings?.scheduleOptions}
              />
            </Box>
          )}

          <PrimaryButton
            boxShadow="button"
            mt={{ _: "6/4", md: 4 }}
            onClick={() => handleBuyButtonClicked()}
            isLoading={isNavigating}
            disabled={
              !seats?.length ||
              (validSeatSelection === false && !isAdmin && !isPOS)
            }
            w="100%"
            fontSize={4}
          >
            {validSeatSelection === true || isAdmin || isPOS
              ? isSinglePage
                ? "Buy tickets"
                : `Buy ${t("membership_name_plural").toLowerCase()}`
              : "Can not leave a single seat"}
          </PrimaryButton>

          <HStack
            mt={2}
            justifyContent="flex-end"
            gap={1}
            flex={1}
            width="100%"
          >
            <ExpressCheckoutButtonRow
              breakpoint={"xs"}
              onClick={(method) => {
                void handleBuyButtonClicked({
                  isExpressCheckout: true,
                  paymentType: method,
                });
              }}
            />
          </HStack>
        </>
      )}
    </Box>
  );
};
